import React, { useEffect, useState } from "react";
import { api } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import SlaSection from "./SlaSection";
import {
  BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer,
  XAxis, YAxis, Tooltip, Legend, ComposedChart, Line
} from "recharts";
import {
  ClipboardList, TrendingUp, AlertTriangle, CheckCircle,
  Brain, RefreshCw, Calendar, ClipboardCheck
} from "lucide-react";

const TYPE_LABELS = {
  commercial_property: "Comm. Property",
  workers_comp: "Workers' Comp",
  habitational: "Habitational",
  general_liability: "Gen. Liability",
  commercial_auto: "Comm. Auto",
};

const STATUS_LABELS = {
  pending: "Pending",
  in_progress: "In Progress",
  completed: "Needs Review",
  finalized: "Finalized",
};

const SEV_COLORS = {
  Critical: "#EF4444",
  High: "#F97316",
  Medium: "#F59E0B",
  Low: "#10B981",
};

const STATUS_COLORS = {
  pending: "#94A3B8",
  in_progress: "#3B82F6",
  completed: "#F59E0B",
  finalized: "#10B981",
};

export default function AnalyticsDashboard() {
  const [data, setData] = useState(null);
  const [recData, setRecData] = useState(null);
  const [slaData, setSlaData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [res, recRes, slaRes] = await Promise.all([
        api.get("/analytics"),
        api.get("/analytics/recommendations").catch(() => ({ data: null })),
        api.get("/analytics/sla").catch(() => ({ data: null })),
      ]);
      setData(res.data);
      setRecData(recRes.data);
      setSlaData(slaRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return (
    <Layout>
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-navy" />
      </div>
    </Layout>
  );

  if (!data) return null;

  const findingsSevData = ["Critical", "High", "Medium", "Low"].map(s => ({
    name: s,
    count: data.findings_by_severity?.[s] || 0,
    fill: SEV_COLORS[s],
  })).filter(d => d.count > 0);

  const typeData = Object.entries(data.by_type || {}).map(([k, v]) => ({
    name: TYPE_LABELS[k] || k,
    surveys: v,
  }));

  const statusData = Object.entries(data.by_status || {}).map(([k, v]) => ({
    name: STATUS_LABELS[k] || k,
    value: v,
    fill: STATUS_COLORS[k] || "#94A3B8",
  }));

  const totalFindings = Object.values(data.findings_by_severity || {}).reduce((a, b) => a + b, 0);

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-800">Analytics</h1>
            <p className="text-slate-500 text-sm mt-0.5">Program performance at a glance</p>
          </div>
          <button onClick={load} className="h-9 w-9 flex items-center justify-center border border-slate-200 rounded-lg text-slate-500 hover:text-navy hover:border-navy transition-colors" data-testid="refresh-analytics-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Total Surveys", value: data.total, icon: ClipboardList, color: "text-navy", bg: "bg-blue-50", testid: "kpi-total" },
            { label: "This Month", value: data.this_month, icon: Calendar, color: "text-blue-600", bg: "bg-blue-50", testid: "kpi-month" },
            { label: "Avg Risk Score", value: data.avg_risk_score ? `${data.avg_risk_score}/100` : "—", icon: TrendingUp, color: "text-orange-600", bg: "bg-orange-50", testid: "kpi-risk" },
            { label: "AI Training Examples", value: data.training_examples, icon: Brain, color: "text-purple-600", bg: "bg-purple-50", testid: "kpi-training" },
          ].map(card => (
            <div key={card.label} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={card.testid}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{card.label}</span>
                <div className={`w-8 h-8 ${card.bg} rounded-lg flex items-center justify-center`}>
                  <card.icon className={`w-4 h-4 ${card.color}`} />
                </div>
              </div>
              <div className="font-heading text-3xl font-bold text-slate-800">{card.value}</div>
            </div>
          ))}
        </div>

        {/* SLA / Turnaround */}
        {slaData && <SlaSection data={slaData} />}

        {/* Charts Row */}
        {/* Recommendation Compliance */}
        {recData && recData.total > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="rec-compliance-section">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading font-semibold text-slate-700 flex items-center gap-2">
                <ClipboardCheck className="w-4 h-4 text-navy" /> Recommendation Compliance
              </h3>
            </div>
            <p className="text-xs text-slate-400 mb-4">Issued vs. closed corrective recommendations — cumulative closure rate over time</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              {[
                { label: "Closure Rate", value: `${recData.closure_rate}%`, color: "text-green-600", tid: "rec-kpi-closure" },
                { label: "Insured Response Rate", value: `${recData.response_rate}%`, color: "text-navy", tid: "rec-kpi-response" },
                { label: "Avg Days to Close", value: recData.avg_days_to_close ?? "—", color: "text-blue-600", tid: "rec-kpi-days" },
                { label: "Open Critical/High", value: (recData.open_by_severity?.critical || 0) + (recData.open_by_severity?.high || 0), color: "text-red-600", tid: "rec-kpi-opencrit" },
              ].map(c => (
                <div key={c.label} className="p-3 bg-slate-50 rounded-lg" data-testid={c.tid}>
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">{c.label}</p>
                  <p className={`font-heading text-2xl font-bold mt-0.5 ${c.color}`}>{c.value}</p>
                </div>
              ))}
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={recData.trend}>
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} width={30} allowDecimals={false} />
                <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 12, fill: "#16A34A" }} axisLine={false} tickLine={false} width={40} unit="%" />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} cursor={{ fill: "#F1F5F9" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="left" dataKey="issued" name="Issued" fill="#1B2B5E" radius={[4, 4, 0, 0]} barSize={22} />
                <Bar yAxisId="left" dataKey="closed" name="Closed" fill="#10B981" radius={[4, 4, 0, 0]} barSize={22} />
                <Line yAxisId="right" type="monotone" dataKey="closure_rate" name="Closure Rate %" stroke="#16A34A" strokeWidth={2.5} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Findings by Severity */}
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="findings-chart">
            <h3 className="font-heading font-semibold text-slate-700 mb-1">Findings by Severity</h3>
            <p className="text-xs text-slate-400 mb-4">{totalFindings} total active findings</p>
            {findingsSevData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={findingsSevData} barSize={40}>
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} width={30} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                    cursor={{ fill: "#F1F5F9" }}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {findingsSevData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-slate-400 text-sm">No findings yet</div>
            )}
          </div>

          {/* Surveys by Type */}
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="type-chart">
            <h3 className="font-heading font-semibold text-slate-700 mb-1">Surveys by Type</h3>
            <p className="text-xs text-slate-400 mb-4">{data.total} total surveys</p>
            {typeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={typeData} layout="vertical" barSize={18}>
                  <XAxis type="number" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} width={110} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} cursor={{ fill: "#F1F5F9" }} />
                  <Bar dataKey="surveys" fill="#1B2B5E" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-slate-400 text-sm">No surveys yet</div>
            )}
          </div>
        </div>

        {/* Status Breakdown + Recent */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Status pie */}
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="status-chart">
            <h3 className="font-heading font-semibold text-slate-700 mb-4">Survey Status Breakdown</h3>
            {statusData.length > 0 ? (
              <div className="flex items-center gap-6">
                <PieChart width={140} height={140}>
                  <Pie data={statusData} cx={65} cy={65} innerRadius={40} outerRadius={65} paddingAngle={3} dataKey="value">
                    {statusData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Pie>
                </PieChart>
                <div className="space-y-2 flex-1">
                  {statusData.map(s => (
                    <div key={s.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: s.fill }} />
                        <span className="text-slate-600">{s.name}</span>
                      </div>
                      <span className="font-semibold text-slate-800">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[140px] flex items-center justify-center text-slate-400 text-sm">No data yet</div>
            )}
          </div>

          {/* Recent completions */}
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="recent-completions">
            <h3 className="font-heading font-semibold text-slate-700 mb-4">Recent Completions</h3>
            {data.recent_completions?.length > 0 ? (
              <div className="space-y-3">
                {data.recent_completions.map(r => (
                  <div key={r.id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
                    <div className="w-7 h-7 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{r.insured_name}</p>
                      <p className="text-xs text-slate-400">{TYPE_LABELS[r.inspection_type] || r.inspection_type}</p>
                    </div>
                    {r.completed_at && (
                      <span className="text-xs text-slate-400 flex-shrink-0">
                        {new Date(r.completed_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[140px] flex items-center justify-center text-slate-400 text-sm">No completions yet</div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
